import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Play, Search, Clock, MonitorPlay, X } from "lucide-react";
import { SiYoutube } from "react-icons/si";
import type { Article } from "@shared/schema";
import { useBrand } from "@/contexts/brand-context";

interface BrandTag {
  slug: string;
  name: string;
  shortName: string | null;
  color: string | null;
  source: string;
  confidence: string | null;
}

interface ArticleWithHero extends Article {
  heroImageUrl?: string | null;
  brandTags?: BrandTag[];
}

const BRAND_BADGE_COLORS: Record<string, string> = {
  "sustainable-engineering-alliance": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  "disti": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  "devkit": "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200",
};

function formatSeconds(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function parseIso8601Duration(iso: string): number | null {
  const match = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (!match) return null;
  const h = parseInt(match[1] || "0", 10);
  const m = parseInt(match[2] || "0", 10);
  const s = parseInt(match[3] || "0", 10);
  return h * 3600 + m * 60 + s;
}

function formatDuration(text: string | null | undefined, fallbackSecs?: number | null): string | null {
  if (text) {
    const trimmed = text.trim();
    if (/^\d+:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed;
    const isoSecs = parseIso8601Duration(trimmed);
    if (isoSecs !== null && isoSecs > 0) return formatSeconds(isoSecs);
    const numSecs = Number(trimmed);
    if (!isNaN(numSecs) && numSecs > 0) return formatSeconds(numSecs);
  }
  if (fallbackSecs && fallbackSecs > 0) return formatSeconds(fallbackSecs);
  return null;
}

function providerLabel(provider: string | null | undefined): string {
  if (!provider) return "Video";
  const map: Record<string, string> = {
    youtube: "YouTube",
    vimeo: "Vimeo",
    wistia: "Wistia",
    brightcove: "Brightcove",
    jwplayer: "JW Player",
  };
  return map[provider.toLowerCase()] ?? provider;
}

function VideoCard({ article }: { article: ArticleWithHero }) {
  const thumbnail =
    article.videoMetadataThumbnailUrl ||
    article.heroImageUrl ||
    article.featuredImage ||
    null;
  const duration = formatDuration(article.videoMetadataDuration, article.videoDuration);
  const provider = providerLabel(article.videoProvider);
  const brandTags = (article.brandTags ?? []).filter(b => b.slug !== "fm");

  const href = article.publishedAt
    ? `/articles/${new Date(article.publishedAt as unknown as string).getFullYear()}/${String(new Date(article.publishedAt as unknown as string).getMonth() + 1).padStart(2, "0")}/${article.slug}`
    : `/articles/${article.slug}`;

  return (
    <Link
      href={href}
      data-testid={`card-video-${article.id}`}
      className="group block rounded-xl border bg-card overflow-hidden hover:shadow-md transition-all hover:border-primary/40"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <MonitorPlay className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}
        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
            <Play className="h-5 w-5 text-slate-900 ml-0.5" fill="currentColor" />
          </div>
        </div>
        {/* Duration badge */}
        {duration && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
            <Clock className="h-2.5 w-2.5" />
            {duration}
          </div>
        )}
      </div>
      {/* Info */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
            <SiYoutube className="h-2.5 w-2.5" />
            {provider}
          </Badge>
          {brandTags.map(bt => (
            <span key={bt.slug} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${BRAND_BADGE_COLORS[bt.slug] ?? "bg-muted text-muted-foreground"}`} data-testid={`badge-brand-${bt.slug}-${article.id}`}>
              {bt.shortName ?? bt.name}
            </span>
          ))}
        </div>
        <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">
          {article.title}
        </h3>
        {article.videoMetadataDescription || article.excerpt ? (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
            {article.videoMetadataDescription || article.excerpt}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

function VideoCardSkeleton() {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <Skeleton className="aspect-video w-full" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );
}

export default function VideosPage() {
  const [search, setSearch] = useState("");
  const { activeBrand, activeBrandMeta, setBrand } = useBrand();

  const { data: articles, isLoading } = useQuery<ArticleWithHero[]>({
    queryKey: ["/api/articles/videos", activeBrand ?? "all"],
    queryFn: async () => {
      const url = activeBrand
        ? `/api/articles/videos?brand=${encodeURIComponent(activeBrand)}&limit=100`
        : "/api/articles/videos?limit=100";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch videos");
      return res.json();
    },
  });

  const videoArticles = articles ?? [];

  const filtered = videoArticles.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      (a.excerpt ?? "").toLowerCase().includes(q) ||
      (a.videoMetadataTitle ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <>
      <Helmet>
        <title>Video Content | Future Manufacturing</title>
        <meta
          name="description"
          content="Watch interviews, product walkthroughs, technology explainers, and industry insights from Future Manufacturing."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Videos" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-7xl px-4">
              <Badge variant="secondary" className="mb-4">Video Content</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                Watch & learn
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mb-8">
                Interviews with industry leaders, product walkthroughs, technology explainers, and event recordings — all in one place.
              </p>
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search videos…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="input-video-search"
                />
              </div>
              {activeBrand && activeBrandMeta && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 border border-white/20 text-sm text-white w-fit" data-testid="status-brand-filter-videos">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activeBrandMeta.tailwindBg}`} />
                  <span>Filtered: <span className="font-semibold">{activeBrandMeta.shortName}</span></span>
                  <button onClick={() => setBrand(null)} className="opacity-70 hover:opacity-100" data-testid="button-dismiss-brand-filter-videos">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Grid */}
          <section className="py-14">
            <div className="mx-auto max-w-7xl px-4">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <VideoCardSkeleton key={i} />
                  ))}
                </div>
              ) : filtered.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-6">
                    {filtered.length} video{filtered.length !== 1 ? "s" : ""}
                    {search.trim() ? ` matching "${search}"` : ""}
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filtered.map((article) => (
                      <VideoCard key={article.id} article={article} />
                    ))}
                  </div>
                </>
              ) : videoArticles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
                    <MonitorPlay className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">No videos yet</h2>
                  <p className="text-muted-foreground max-w-sm">
                    Video content will appear here as it is published. Check back soon.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <Search className="h-10 w-10 text-muted-foreground mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No results found</h2>
                  <p className="text-muted-foreground">
                    Try a different search term.
                  </p>
                </div>
              )}
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
